import React, {useState, useEffect} from 'react';
import {t} from 'i18next';
import {Grid, Typography, Box} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import './AddClient.scss';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {
  useGetLoadEntityData,
  useGetAddEntityData,
  useGetClientData,
} from '../../../hooks/clients/useClients';
import {
  DropDownItem,
  EntityRequestParams,
  IClient,
  ListItem,
} from '../../../api/types/client.type';
import z from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../src/redux/store';
import {ToastType} from '../../../shared/constants';
import {errorMaps} from '../../../utils/custom-errors/errorMessages';
import ArrModal from '../../common/arr-modal/ArrModal';

function filterAndSelect(array: any, keys: any) {
  const updatedValues = array.map((item: any) => ({
    ...item,
    selected: keys.includes(item.value),
    htmlAttributes: 'string',
  }));
  return updatedValues;
}

const code25001000001 = '25001000001';
const code25001000002 = '25001000002';
const code25001000003 = '25001000003';

const fieldApiErrors = [
  errorMaps.ClientManagement[code25001000001],
  errorMaps.ClientManagement[code25001000002],
  errorMaps.ClientManagement[code25001000003],
];

const getFieldName = ({code = ''}) => {
  switch (code) {
    case code25001000001:
      return 'shortName';
    case code25001000002:
      return 'entityName';
    case code25001000003:
      return 'domainId';
    default:
      break;
  }
};

export const clientSchema = z
  .object({
    entityName: z
      .string()
      .min(1, t('manageClients.addClient.validations.clientRequiredError'))
      .max(50, t('manageClients.addClient.validations.clientLengthError'))
      .regex(/^[a-zA-Z0-9 _-]+$/, {
        message: t('manageClients.addClient.validations.SpecialError'),
      })
      .refine((val) => val.trim().length > 0, {
        message: t('manageClients.addClient.validations.clientRequiredError'),
      }),
    domainId: z
      .string()
      .trim()
      .min(1, t('manageClients.addClient.validations.domainIdRequiredError'))
      .regex(
        /^[a-zA-Z0-9 _-]+$/,
        t('manageClients.addClient.validations.SpecialError')
      ),
    shortName: z
      .string()
      .min(1, t('manageClients.addClient.validations.shortNameRequiredError'))
      .regex(
        /^[a-zA-Z0-9]+$/,
        t('manageClients.addClient.validations.SpecialError')
      )
      .length(4, t('manageClients.addClient.validations.shortNameLengthError')),
    trueLawBaseUrl: z.string().optional(),
    trueLawClientKey: z.string().optional(),
    isTrueLawEnabled: z.boolean().optional(),
    canUseGenAI: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isTrueLawEnabled) {
      if (!data.trueLawBaseUrl?.trim()) {
        ctx.addIssue({
          path: ['trueLawBaseUrl'],
          message: t(
            'manageClients.addClient.validations.trueLawBaseUrlRequired'
          ),
          code: z.ZodIssueCode.custom,
        });
      }

      if (!data.trueLawClientKey?.trim()) {
        ctx.addIssue({
          path: ['trueLawClientKey'],
          message: t(
            'manageClients.addClient.validations.trueLawClientKeyRequired'
          ),
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });
type ClientFormValues = z.infer<typeof clientSchema>;

type AddClientProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
  onClientAdded?: () => void;
  selectedClient?: IClient | null;
  isEdit: boolean;
  triggerToast: (message: string, status: ToastType[]) => void;
};
interface DropDownState {
  ingestionWorkspace: DropDownItem[];
  projectWorkspace: DropDownItem[];
  productionWorkspace: DropDownItem[];
}
const fullWidthGridStyle = {xs: 12, sm: 12};

// ============AddClient Component============
export const AddClient: React.FC<AddClientProps> = ({
  showDrawer,
  handleDrawer,
  selectedClient = null,
  isEdit,
  triggerToast,
}) => {
  const {data: loadEntityData, isSuccess: isLoadEntitySuccess} =
    useGetLoadEntityData('defaultKey');
  const {data: clientData} = useGetClientData(
    {
      entityId: selectedClient?.entityId ?? 0,
      entityCode: selectedClient?.entityCode ?? '',
      entityLabel: selectedClient?.entityLabel ?? '',
    },
    isEdit && isLoadEntitySuccess
  );

  const [dropdownState, setDropdownState] = useState<DropDownState>({
    ingestionWorkspace: [],
    projectWorkspace: [],
    productionWorkspace: [],
  });

  // ===================ClientForm===============
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    setError,
    formState: {errors, isValid},
  } = useForm({
    resolver: zodResolver(clientSchema),
    mode: 'onChange',
    defaultValues: {
      entityName:
        isEdit && clientData?.entityModelData.entityName
          ? clientData.entityModelData.entityName
          : '',
      domainId:
        isEdit && clientData?.entityModelData.entityDomainID
          ? clientData.entityModelData.entityDomainID
          : '',
      shortName:
        isEdit && clientData?.entityModelData.entityCode
          ? clientData.entityModelData.entityCode
          : '',
      isTrueLawEnabled:
        isEdit && clientData?.entityModelData.isTrueLawEnabled
          ? clientData.entityModelData.isTrueLawEnabled
          : false,
      trueLawClientKey:
        isEdit && clientData?.entityModelData.trueLawClientKey
          ? clientData.entityModelData.trueLawClientKey
          : '',
      trueLawBaseUrl:
        isEdit && clientData?.entityModelData.trueLawBaseUrl
          ? clientData.entityModelData.trueLawBaseUrl
          : '',
    },
  });

  const [workspaceErrors, setWorkspaceErrors] = useState({
    project: '',
    ingestion: '',
    production: '',
  });
  const [workspaceTouched, setWorkspaceTouched] = useState({
    project: false,
    ingestion: false,
    production: false,
  });
  const [isworkspace, setIsWorkspace] = useState(false);
  const [isTrueLawEnabled, setisTrueLawEnabled] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<string[]>([]);
  const [selectedIngestion, setSelectedIngestion] = useState<string[]>([]);
  const [databaseSizeCode, setDatabaseSizeCode] = useState<string>('');
  const [domainSizeCode, setDomainSizeCode] = useState<string>('');
  const [isGenerativeAI, setIsGenerativeAI] = useState<boolean>(false);
  const [enableGenerativeAIModalTitle, setEnableGenerativeAIModalTitle] =
    useState<string>('');
  const [enableGenerativeAIModalDesc, setEnableGenerativeAIModalDesc] =
    useState<string>('');
  const {mutate: addentity} = useGetAddEntityData();
  const trueLawBaseUrl = watch('trueLawBaseUrl');
  const trueLawClientKey = watch('trueLawClientKey');

  const {
    user: {userId, userName},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const handleProduction = (values: string[] | null) => {
    setWorkspaceTouched((prevState) => ({...prevState, production: true}));
    setSelectedProduction(values ?? []);
  };
  const handleProject = (values: string[] | null) => {
    setWorkspaceTouched((prevState) => ({...prevState, project: true}));
    setSelectedProject(values ?? []);
  };
  const handleIngestion = (values: string[] | null) => {
    setWorkspaceTouched((prevState) => ({...prevState, ingestion: true}));
    setSelectedIngestion(values ?? []);
  };
  const validateWorkspaces = () => {
    setWorkspaceErrors({
      project:
        projectDBServerList.length > 0 && selectedProject.length === 0
          ? t('manageClients.addClient.validations.validateProject')
          : '',
      ingestion:
        ingestionWorkspaceOptions.length > 0 && selectedIngestion.length === 0
          ? t('manageClients.addClient.validations.validateIngestion')
          : '',
      production:
        productionWorkspaceOptions.length > 0 && selectedProduction.length === 0
          ? t('manageClients.addClient.validations.validateProduction')
          : '',
    });
  };
  // *** Processing Dropdown Data ***
  const ingestionWorkspaceOptions = React.useMemo<ListItem[]>(() => {
    if (!loadEntityData) return [];
    return (
      loadEntityData.bindDropDowns?.ingestionLoadWorkspace
        ?.filter((item) => item.value !== '0')
        .map(
          (item): ListItem => ({
            id: String(item.value),
            label: item.text,
            text: item.text,
            selected: false,
          })
        ) || []
    );
  }, [loadEntityData]);

  const projectDBServerList = React.useMemo<ListItem[]>(() => {
    if (!loadEntityData) return [];
    return (
      loadEntityData.bindDropDowns.projectWorkspace
        ?.filter((item) => item.value !== '0')
        .map(
          (item): ListItem => ({
            id: String(item.value),
            label: item.text,
            text: item.text,
            selected: false,
          })
        ) || []
    );
  }, [loadEntityData]);
  const productionWorkspaceOptions = React.useMemo<ListItem[]>(() => {
    if (!loadEntityData) return [];
    return (
      loadEntityData.bindDropDowns?.productionWorkspace
        ?.filter((item) => item.value !== '0')
        .map(
          (item): ListItem => ({
            id: String(item.value),
            label: item.text,
            text: item.text,
            selected: false,
          })
        ) || []
    );
  }, [loadEntityData]);

  useEffect(() => {
    if (!loadEntityData) return;
    setDropdownState({
      ingestionWorkspace:
        loadEntityData.bindDropDowns?.ingestionLoadWorkspace || [],
      projectWorkspace: loadEntityData.bindDropDowns?.projectWorkspace || [],
      productionWorkspace:
        loadEntityData.bindDropDowns?.productionWorkspace || [],
    });
    setDatabaseSizeCode(
      loadEntityData.bindDropDowns?.clientDefaultDatabaseSizeCode || ''
    );
    setDomainSizeCode(
      loadEntityData.bindDropDowns?.domainDefaultDatabaseSizeCode || ''
    );
  }, [loadEntityData]);

  useEffect(() => {
    validateWorkspaces();
  }, [
    selectedProject,
    selectedIngestion,
    selectedProduction,
    projectDBServerList.length,
    ingestionWorkspaceOptions.length,
    productionWorkspaceOptions.length,
  ]);

  const isSaveEnabled = React.useMemo(() => {
    if (!isValid) return false;
    if (isTrueLawEnabled) {
      if (!trueLawBaseUrl?.trim()) return false;
      if (!trueLawClientKey?.trim()) return false;
    }
    if (projectDBServerList.length > 0 && selectedProject.length === 0)
      return false;
    if (ingestionWorkspaceOptions.length > 0 && selectedIngestion.length === 0)
      return false;
    if (
      productionWorkspaceOptions.length > 0 &&
      selectedProduction.length === 0
    ) {
      return false;
    }
    return true;
  }, [
    isValid,
    selectedProject,
    selectedIngestion,
    selectedProduction,
    projectDBServerList.length,
    ingestionWorkspaceOptions.length,
    productionWorkspaceOptions.length,
    isTrueLawEnabled,
    trueLawBaseUrl,
    trueLawClientKey,
  ]);

  const initializeWorkSpaces = () => {
    if (projectDBServerList.length > 0) {
      if (isEdit) {
        const calculatedKeys =
          clientData?.entityModelData.domainEntityCommonSettingsDetails
            .selectedProjectFileServers ?? [];
        setSelectedProject(calculatedKeys);
        setWorkspaceTouched((prevState) => ({...prevState, project: true}));
      } else {
        if (projectDBServerList.length === 1) {
          setSelectedProject([projectDBServerList[0].id]);
          setWorkspaceTouched((prevState) => ({...prevState, project: true}));
        }
      }
    }
    if (ingestionWorkspaceOptions.length > 0) {
      if (isEdit) {
        const calculatedKeys =
          clientData?.entityModelData.domainEntityCommonSettingsDetails
            .selectedLoadFileServers ?? [];
        setSelectedIngestion(calculatedKeys);
        setWorkspaceTouched((prevState) => ({...prevState, ingestion: true}));
      } else {
        if (ingestionWorkspaceOptions.length === 1) {
          setSelectedIngestion([ingestionWorkspaceOptions[0].id]);
          setWorkspaceTouched((prevState) => ({...prevState, ingestion: true}));
        }
      }
    }
    if (productionWorkspaceOptions.length > 0) {
      if (isEdit) {
        const calculatedKeys =
          clientData?.entityModelData.domainEntityCommonSettingsDetails
            .selectedProductionFileServers ?? [];
        setSelectedProduction(calculatedKeys);
        setWorkspaceTouched((prevState) => ({
          ...prevState,
          production: true,
        }));
      } else {
        if (productionWorkspaceOptions.length === 1) {
          setSelectedProduction([productionWorkspaceOptions[0].id]);
          setWorkspaceTouched((prevState) => ({
            ...prevState,
            production: true,
          }));
        }
      }
    }
    if (isEdit) {
      setIsWorkspace(!!clientData?.entityModelData.isInternal);
      setisTrueLawEnabled(!!clientData?.entityModelData.isTrueLawEnabled);
    }
  };

  const handleReset = () => {
    setDatabaseSizeCode('');
    setDomainSizeCode('');
    setIsWorkspace(false);
    setIsGenerativeAI(false);
    handleProduction([]);
    handleIngestion([]);
    handleProject([]);
    reset({
      entityName: '',
      domainId: '',
      shortName: '',
      isTrueLawEnabled: false,
      trueLawClientKey: '',
      trueLawBaseUrl: '',
    });
  };

  useEffect(() => {
    if (showDrawer) {
      reset({
        entityName: isEdit
          ? (clientData?.entityModelData.entityName ?? '')
          : '',
        domainId: isEdit
          ? (clientData?.entityModelData.entityDomainID ?? '')
          : '',
        shortName: isEdit ? (clientData?.entityModelData.entityCode ?? '') : '',
        isTrueLawEnabled: isEdit
          ? (clientData?.entityModelData.isTrueLawEnabled ?? false)
          : false,
        trueLawClientKey: isEdit
          ? (clientData?.entityModelData.trueLawClientKey ?? '')
          : '',
        trueLawBaseUrl: isEdit
          ? (clientData?.entityModelData.trueLawBaseUrl ?? '')
          : '',
      });

      setIsGenerativeAI(
        isEdit ? !!clientData?.entityModelData?.canUseGenAI : false
      );

      initializeWorkSpaces();
    }
  }, [
    showDrawer,
    isEdit,
    clientData,
    projectDBServerList.length,
    ingestionWorkspaceOptions.length,
    productionWorkspaceOptions.length,
  ]);

  useEffect(() => {
    if (!showDrawer) handleReset();
  }, [showDrawer]);

  const onChangeTruelawToggle = (checked: boolean) => {
    setisTrueLawEnabled(checked);
    if (!checked) {
      setValue('trueLawBaseUrl', '');
      setValue('trueLawClientKey', '');
      clearErrors(['trueLawBaseUrl', 'trueLawClientKey']);
    }
  };

  const onSubmit = async (data: ClientFormValues) => {
    const errors = {
      project:
        projectDBServerList.length > 0 && selectedProject.length === 0
          ? t('manageClients.addClient.validations.validateProject')
          : '',
      ingestion:
        ingestionWorkspaceOptions.length > 0 && selectedIngestion.length === 0
          ? t('manageClients.addClient.validations.validateIngestion')
          : '',
      production:
        productionWorkspaceOptions.length > 0 && selectedProduction.length === 0
          ? t('manageClients.addClient.validations.validateProduction')
          : '',
    };
    setWorkspaceErrors(errors);
    // stop submission if any error exists
    if (errors.project || errors.ingestion || errors.production) return;

    const request: EntityRequestParams = {
      trueLawBaseUrl: data.trueLawBaseUrl || '',
      trueLawClientKey: data.trueLawClientKey || '',
      isTrueLawEnabled: isTrueLawEnabled,
      domainEntityCommonSettingsDetails: {
        projectDBServerID: 0,
        dataBaseServerID: 0,
        selectedDataBaseServerID: 0,
        dataBaseServerLabel: 'DB Server',
        fileServerID: 0,
        fileServerLabel: 'File Server',
        projectFileServers: filterAndSelect(
          dropdownState.projectWorkspace,
          selectedProject
        ),
        selectedProjectFileServers: selectedProject,
        loadFileServers: filterAndSelect(
          dropdownState.ingestionWorkspace,
          selectedIngestion
        ),
        selectedLoadFileServers: selectedIngestion,
        productionFileServers: filterAndSelect(
          dropdownState.productionWorkspace,
          selectedProduction
        ),
        selectedProductionFileServers: selectedProduction,
        iceInstanceID: isEdit
          ? (clientData?.iceInstanceServerData?.[0].key ?? 0)
          : 0,
        selectedICEInstanceID: isEdit
          ? (clientData?.iceInstanceServerData?.[0].key ?? 0)
          : 0,
        iceInstanceName: isEdit
          ? (clientData?.iceInstanceServerData?.[0].value ?? '')
          : '',
        canEditICEInstanceOfEntity: isEdit,
        projectDBSizeID: domainSizeCode,
        selectedProjectDBSizeID: '',
        canEditProjectDBSizeInstance: false,
        clientDefaultDatabaseSizeCode: databaseSizeCode,
        domainDefaultDatabaseSizeCode: domainSizeCode,
        processingEngineTypeCode: 'ENGINE_X',
      },
      // Need to pass the existing one
      entityId:
        isEdit && selectedClient?.entityId ? selectedClient?.entityId : 0,
      entityName: data.entityName,
      entityCode: data.shortName,
      entityLabel: data.entityName,
      isActive: true,
      isEntityExists: false,
      isDeleted: false,
      createdByID: userId,
      createdByName: userName,
      modifiedByID: userId,
      createdOn: new Date().toISOString(),
      modifiedOn: new Date().toISOString(),
      userId: userId,
      isEntityEdited: isEdit,
      rowNumber: 1,
      entityType: '2',
      entityDomainID: data.domainId,
      ssoEnabled: false,
      ssoTenantID: '',
      ssoTenantDomain: '',
      ssoUrl: '',
      shouldUseCyberView: true,
      isInternal: isworkspace,
      canUseGenAI: isGenerativeAI,
    };
    addentity(request, {
      onSuccess: () => {
        triggerToast(
          isEdit
            ? t('manageClients.addClient.updateSaveMessage')
            : t('manageClients.addClient.successSaveMessage'),
          [ToastType.Success]
        );
        handleDrawer(false);
        handleReset();
      },
      onError: (data) => {
        if (fieldApiErrors.includes(data.message)) {
          setError(getFieldName(data), {
            type: 'manual',
            message: data.message,
          });
        } else {
          triggerToast(data.message || t('securityGroup.failureMessage'), [
            ToastType.Error,
          ]);
        }
      },
    });
  };
  const contentBody = (
    <form
      aria-labelledby="add-client-title"
      onSubmit={handleSubmit(onSubmit)}
      id="client-form"
      noValidate
      data-testid={`add-client-cancel-save-toggle-generative-ai-toggle-button-title-workspace-project-list-ingestion-list-production-list-true-law`}
    >
      <Box className="add-client-container">
        <Box className="add-client-header-box">
          <Box className="title-box">
            <Typography
              className="typo-text headline3_size header-title"
              tabIndex={0}
              data-testid={`add-client-title`}
            >
              {isEdit
                ? t('manageClients.addClient.editTitle')
                : t('manageClients.addClient.title')}
              &nbsp;
            </Typography>
            {!isEdit && (
              <ArrTooltipWrapper
                title={t('manageClients.addClient.tooltipTitle')}
                trigger={<InfoIcon className="info-icon" tabIndex={0} />}
              >
                <p data-testid={`add-client-tt0`}>
                  {t('manageClients.addClient.titletoolTip.tt0')}
                </p>
                <p data-testid={`add-client-tt1`}>
                  {t('manageClients.addClient.titletoolTip.tt1')}
                </p>
              </ArrTooltipWrapper>
            )}
          </Box>

          <Box className="action-buttons-box">
            <ArrButton
              tabIndex={0}
              variant="secondary"
              size="medium"
              onClick={() => handleDrawer(false)}
              dataTestId="add-client-cancel-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              type="submit"
              disabled={!isSaveEnabled}
              tabIndex={isSaveEnabled ? 0 : -1}
              form="client-form"
              dataTestId="add-client-save-button"
            >
              {t('common.save')}
            </ArrButton>
          </Box>
        </Box>
        <Box className="content-box">
          <Grid container columnSpacing={4} rowSpacing={3}>
            <Grid size={{xs: 12, sm: 6}}>
              <ArrTextField
                required
                label={t('manageClients.addClient.lname')}
                {...register('entityName')}
                value={watch('entityName')}
                state={errors.entityName ? 'error' : 'enabled'}
                supportText={errors.entityName?.message?.toString() ?? ''}
                dataTestId="add-client-name"
              />
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <ArrTextField
                required
                label={t('manageClients.addClient.ldomainID')}
                {...register('domainId')}
                value={watch('domainId')}
                state={errors.domainId ? 'error' : 'enabled'}
                supportText={errors.domainId?.message?.toString() ?? ''}
                dataTestId="add-client-domain-id"
              />
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <ArrTextField
                required
                label={t('manageClients.addClient.lshortName')}
                {...register('shortName')}
                value={watch('shortName')}
                state={
                  isEdit ? 'inactive' : errors.shortName ? 'error' : 'enabled'
                }
                supportText={errors?.shortName?.message?.toString() ?? ''}
                aria-invalid={!!errors.shortName}
                dataTestId="add-client-short-name"
              />
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <Box display="flex" alignItems="center" gap={0}></Box>
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <Box display="flex" alignItems="center" gap={0}>
                <ArrToggle
                  checked={isGenerativeAI}
                  onChange={(e, checked) => {
                    if (isGenerativeAI) {
                      setEnableGenerativeAIModalTitle(
                        t(
                          'manageClients.addClient.disableGenerativeAIModalTitle'
                        )
                      );
                      setEnableGenerativeAIModalDesc(
                        t(
                          'manageClients.addClient.disableGenerativeAIModalDesc'
                        )
                      );
                    } else {
                      setEnableGenerativeAIModalTitle(
                        t(
                          'manageClients.addClient.enableGenerativeAIModalTitle'
                        )
                      );
                      setEnableGenerativeAIModalDesc(
                        t('manageClients.addClient.enableGenerativeAIModalDesc')
                      );
                    }
                    setIsGenerativeAI(checked);
                  }}
                  dataTestId="ai-modal-toggle"
                />
                <Typography
                  className="typo-text headline5_size title"
                  tabIndex={0}
                  data-testid={`add-client-toggle-generative-ai`}
                >
                  {t('manageClients.addClient.toggleGenerativeAI')}
                </Typography>
                <ArrTooltipWrapper
                  title={t('manageClients.addClient.enableGenerativeAI')}
                  threshold={50}
                  trigger={
                    <InfoIcon
                      fontSize="medium"
                      aria-label="information"
                      className="info-icon-small toggle-generative-ai-tooltip"
                      data-testid="toggleGenerativeAI-tooltip-info"
                      tabIndex={0}
                    />
                  }
                >
                  <div data-testid="toggleGenerativeAI-Tooltip-content">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'manageClients.addClient.tooltipworkspace.tt2'
                        ),
                      }}
                      data-testid={`add-client-p`}
                    />
                  </div>
                </ArrTooltipWrapper>
              </Box>
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <Box display="flex" alignItems="center" gap={0}>
                <ArrToggle
                  checked={isworkspace}
                  onChange={(e, checked) => setIsWorkspace(checked)}
                  dataTestId="workspace-toggle"
                />
                <Typography
                  className="typo-text headline5_size title"
                  tabIndex={0}
                  data-testid={`add-client-toggle-button-title`}
                >
                  {t('manageClients.addClient.toggleButtonTitle')}
                </Typography>
              </Box>
            </Grid>

            <Grid size={fullWidthGridStyle}>
              <Box
                className="client-workspace-container"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0,
                }}
              >
                <Typography
                  className="typo-text subhead4_size title"
                  data-testid={`add-client-workspace`}
                >
                  {t('projectManagement.workspace')}&nbsp;
                </Typography>
                <ArrTooltipWrapper
                  title={t('manageClients.addClient.tooltipworkspace.title')}
                  threshold={50}
                  trigger={
                    <InfoIcon
                      fontSize="medium"
                      aria-label="information"
                      className="info-icon-small"
                      data-testid="step1-tooltip-info"
                      tabIndex={0}
                    />
                  }
                >
                  <div data-testid="step1Tooltip-content">
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'manageClients.addClient.tooltipworkspace.tt1'
                        ),
                      }}
                      data-testid={`add-client-p-text`}
                    />
                  </div>
                </ArrTooltipWrapper>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                {/* ---------- PROJECT LIST ---------- */}
                <Typography
                  className="typo-text subhead4_size title"
                  sx={{mt: 1}}
                  tabIndex={0}
                  data-testid={`add-client-project-list`}
                >
                  {t('manageClients.addClient.projectList')}
                </Typography>
                <Box className="client-checkbox-box">
                  <ArrTreeView
                    treeSelectedItems={selectedProject}
                    setSelectedItem={handleProject}
                    itemsList={projectDBServerList}
                    isShowCheckbox={true}
                    multipleSelection={true}
                    isShowFileType={false}
                  />
                </Box>
                {workspaceErrors.project && workspaceTouched.project && (
                  <Typography
                    className="typo-text error-text"
                    data-testid={`add-client-error-project-text`}
                  >
                    {workspaceErrors.project}
                  </Typography>
                )}
                {/* ---------- INGESTION LIST ---------- */}
                <Typography
                  className="typo-text subhead4_size title"
                  sx={{mt: 1}}
                  tabIndex={0}
                  data-testid={`add-client-ingestion-list`}
                >
                  {t('manageClients.addClient.ingestionList')}
                </Typography>
                <Box className="client-checkbox-box">
                  <ArrTreeView
                    treeSelectedItems={selectedIngestion}
                    setSelectedItem={handleIngestion}
                    itemsList={ingestionWorkspaceOptions}
                    isShowCheckbox={true}
                    multipleSelection={true}
                    isShowFileType={false}
                  />
                </Box>
                {workspaceErrors.ingestion && workspaceTouched.ingestion && (
                  <Typography
                    className="typo-text error-text"
                    data-testid={`add-client-ingestion`}
                  >
                    {workspaceErrors.ingestion}
                  </Typography>
                )}
                <Typography
                  className="typo-text subhead4_size title"
                  tabIndex={0}
                  sx={{mt: 1}}
                  data-testid={`add-client-production-list`}
                >
                  {t('manageClients.addClient.productionList')}
                </Typography>
                <Box className="client-checkbox-box" sx={{m: 0, p: 0}}>
                  <ArrTreeView
                    treeSelectedItems={selectedProduction}
                    setSelectedItem={handleProduction}
                    itemsList={productionWorkspaceOptions}
                    isShowCheckbox={true}
                    multipleSelection={true}
                    isShowFileType={false}
                  />
                </Box>
              </Box>
              {workspaceErrors.production && workspaceTouched.production && (
                <Typography
                  className="typo-text error-text"
                  data-testid={`add-client-workspace-production`}
                >
                  {workspaceErrors.production}
                </Typography>
              )}
              <Box display="flex" alignItems="center" gap={1} sx={{mt: 1}}>
                <Typography
                  className="typo-text subhead4_size title"
                  tabIndex={0}
                  data-testid={`add-client-true-law`}
                >
                  {t('manageClients.addClient.trueLaw')}
                </Typography>

                <ArrToggle
                  checked={isTrueLawEnabled}
                  {...register('isTrueLawEnabled')}
                  onChange={(e, checked) => onChangeTruelawToggle(checked)}
                  dataTestId="law-enabled-toggle"
                />
              </Box>
              {isTrueLawEnabled && (
                <Grid container direction="column" spacing={2}>
                  <Grid size={{xs: 12}}>
                    <ArrTextField
                      required
                      label={t('manageClients.addClient.baseUrl')}
                      {...register('trueLawBaseUrl')}
                      value={watch('trueLawBaseUrl')}
                      state={errors.trueLawBaseUrl ? 'error' : 'enabled'}
                      supportText={
                        errors.trueLawBaseUrl?.message?.toString() ?? ''
                      }
                      dataTestId="add-client-base-url"
                    />
                  </Grid>

                  <Grid size={{xs: 12}}>
                    <ArrTextField
                      required
                      type="password"
                      label={t('manageClients.addClient.clientKey')}
                      {...register('trueLawClientKey')}
                      value={watch('trueLawClientKey')}
                      state={errors.trueLawClientKey ? 'error' : 'enabled'}
                      supportText={
                        errors.trueLawClientKey?.message?.toString() ?? ''
                      }
                      dataTestId="add-client-client-key"
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </form>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showButton={false}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
          showHeader={false}
        >
          {contentBody}
        </ArrDrawer>
      )}
      {enableGenerativeAIModalDesc && (
        <ArrModal
          showModal={!!enableGenerativeAIModalDesc}
          onClose={() => setEnableGenerativeAIModalDesc('')}
          title={enableGenerativeAIModalTitle}
          description={enableGenerativeAIModalDesc}
          autoFocusCloseButton={true}
          dataTestId="enableGenerativeAI-modal"
          buttons={[
            {
              id: '1',
              label: t('common.close'),
              onClick: () => setEnableGenerativeAIModalDesc(''),
              variant: 'primary',
              dataTestId: 'enableGenerativeAI-modal-close-btn',
            },
          ]}
        />
      )}
    </>
  );
};
